/**
 * Component Price Types
 *
 * TypeScript types for component price management.
 * These types match the backend DTOs for seamless integration.
 */

/**
 * Component price list item DTO
 * Used in paginated list views
 */
export interface ComponentPriceListDto {
  id: string;
  componentId: string;
  componentName: string;
  region?: string;
  pricePerUnit: number;
  currency: string;
  billingFrequency: BillingFrequency;
  effectiveFrom: string;
  effectiveTo?: string;
  isActive: boolean;
}

/**
 * Component price detail DTO
 * Used in detail views and after create/update operations
 */
export interface ComponentPriceDetailDto {
  id: string;
  componentId: string;
  componentName: string;
  productId?: string;
  region?: string;
  pricePerUnit: number;
  currency: string;
  billingFrequency: BillingFrequency;
  minQuantity?: number;
  maxQuantity?: number;
  effectiveFrom: string;
  effectiveTo?: string;
  isActive: boolean;
  metadata?: string;
  tierCount: number;
  createdAt: string;
  modifiedAt?: string;
}

/**
 * Create component price request DTO
 */
export interface CreateComponentPriceRequest {
  productId?: string;
  region?: string;
  pricePerUnit: number;
  currency: string;
  billingFrequency: BillingFrequency;
  minQuantity?: number;
  maxQuantity?: number;
  effectiveFrom: string;
  effectiveTo?: string;
  metadata?: string;
}

/**
 * Update component price request DTO
 */
export interface UpdateComponentPriceRequest {
  pricePerUnit: number;
  effectiveTo?: string;
  isActive: boolean;
  metadata?: string;
}

/**
 * Billing frequency — mirrors backend WiCloud.Domain.Enums.BillingFrequency
 * Values: Hourly, Daily, Monthly, Quarterly, Annually (stored as PascalCase strings)
 */
export const BILLING_FREQUENCIES = [
  "Hourly",
  "Daily",
  "Monthly",
  "Quarterly",
  "Annually",
] as const;
export type BillingFrequency = (typeof BILLING_FREQUENCIES)[number];

/**
 * Component price filters for list queries
 * Only includes parameters the backend actually accepts.
 */
export interface ComponentPriceFilters {
  region?: string;
  currency?: string;
  billingFrequency?: BillingFrequency;
  isActive?: boolean;
  effectiveDate?: string;
  page?: number;
  limit?: number;
}
